import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { NewsPreferences } from "@/hooks/useNewsPreferences";
import { useContacts } from "@/hooks/useContacts";
import { Building2 } from "lucide-react";

interface CompaniesTabProps {
  preferences: NewsPreferences;
  onChange: (updates: Partial<NewsPreferences>) => void;
  additionalCompaniesInput: string;
  onAdditionalCompaniesInputChange: (value: string) => void;
}

export const CompaniesTab = ({ preferences, onChange, additionalCompaniesInput, onAdditionalCompaniesInputChange }: CompaniesTabProps) => {
  const { contacts } = useContacts();

  // Extract unique companies from contacts
  const contactCompanies = Array.from(
    new Set(
      contacts
        ?.filter(c => c.company)
        .map(c => c.company!.name) || []
    )
  );


  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-base font-semibold">Track Contact Companies</Label>
            <p className="text-sm text-muted-foreground">
              Automatically search news for companies in your contacts
            </p>
          </div>
          <Switch
            checked={preferences.track_contact_companies}
            onCheckedChange={(checked) => onChange({ track_contact_companies: checked })}
          />
        </div>

        {preferences.track_contact_companies && contactCompanies.length > 0 && (
          <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Building2 className="h-4 w-4" />
              <span>Currently Tracking from Contacts:</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {contactCompanies.map((company, index) => (
                <div key={index} className="text-sm text-muted-foreground">
                  • {company}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {contactCompanies.length} {contactCompanies.length === 1 ? 'company' : 'companies'} from your contacts
            </p>
          </div>
        )}

        {preferences.track_contact_companies && contactCompanies.length === 0 && (
          <div className="rounded-lg border border-dashed bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground text-center">
              No companies found in your contacts yet. Add contacts with company information to enable automatic tracking.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <Label className="text-base font-semibold">Additional Companies to Track</Label>
          <p className="text-sm text-muted-foreground">
            Add companies not in your contacts (comma-separated, max 20)
          </p>
          <Input
            value={additionalCompaniesInput}
            onChange={(e) => onAdditionalCompaniesInputChange(e.target.value)}
            placeholder="e.g., Caterpillar, Komatsu, Hilti, DeWalt"
          />
          <p className="text-xs text-muted-foreground">
            Additional companies: {additionalCompaniesInput.split(',').filter(c => c.trim()).length} / 20
          </p>
        </div>
      </div>
    </div>
  );
};
